import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { requestPasswordReset } from '../services/api';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import toast from 'react-hot-toast';

const forgotPasswordSchema = z.object({
  email: z.string().min(1, 'Email address is required').email('Invalid email address'),
});

const ForgotPasswordPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onChange',
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      const response = await requestPasswordReset(data.email);
      const { data: responseData } = response;

      if (responseData.status) {
        toast.success(responseData.message || 'Password reset link sent to your email');
        // Stay on page or navigate to login. Let's navigate to login since they need to check email.
        setTimeout(() => navigate('/login'), 2000);
      } else {
        toast.error(responseData.message || 'Failed to request password reset');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50 relative overflow-hidden font-sans">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3 pointer-events-none"></div>

      <div className="w-full max-w-md p-8 sm:p-10 space-y-8 glass rounded-2xl z-10 animate-slide-up m-4">
        <div className="text-center space-y-2">
          <div className="w-12 h-12 bg-primary text-white rounded-xl flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg shadow-primary/30">
            W
          </div>
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Forgot Password</h2>
          <p className="text-slate-500 text-sm">Enter your email address to receive a password reset link.</p>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <Input
            label="Email Address"
            id="email"
            type="email"
            placeholder="Enter your email address"
            {...register('email')}
            error={errors.email?.message}
          />

          <Button
            type="submit"
            className="w-full"
            size="lg"
            isLoading={isLoading}
          >
            Reset Password
          </Button>

          <button
            type="button"
            className="w-full text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
            onClick={() => navigate('/login')}
          >
            ← Back to Login
          </button>
        </form>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
